<script
    setup
    lang="ts"
>
import { generateRandomID } from "@/services/utils";
import { computed, ref } from "vue";
import OkIcon from "@/components/icons/OkIcon.vue";
import TrashIcon from "@/components/icons/TrashIcon.vue";

const randomId = generateRandomID();

const props = defineProps({
  accept: {
    type: String,
    default: "",
  },
  imageOnly: {
    type: Boolean,
    default: false,
  },
  preview: {
    type: String,
    default: null,
  },
});

const accept = computed(() => {
  if (props.imageOnly) {
    return "image/*";
  }

  return props.accept;
});

const emit = defineEmits(["update:modelValue", 'change']);

const loadedImage = ref(props.preview || null);
const loadedFileName = ref('');
const loadedFileSize = ref(0);

const isFileSelected = computed(() => Boolean(loadedFileName.value) && !loadedImage.value);

const loadedFileExtension = computed(() => {
  const parts = loadedFileName.value.split('.');
  if (parts.length < 2) return 'file';
  return parts.pop().toLowerCase();
});

const loadedFileSizeLabel = computed(() => {
  const sizeInBytes = loadedFileSize.value;
  const sizeInKb = sizeInBytes / 1024;
  const sizeInMb = sizeInKb / 1024;

  if (sizeInMb > 1) {
    return `${sizeInMb.toFixed(2)} MB`;
  }
  if (sizeInKb > 1) {
    return `${sizeInKb.toFixed(2)} KB`;
  }
  return `${sizeInBytes} B`;
});

const handleFileChange = (e: Event) => {
  e.preventDefault();
  const file = (e.target as HTMLInputElement).files?.[0];
  if (!file) return;

  emit("change", file);

  const reader = new FileReader();
  reader.onload = (e) => {
    loadedImage.value = e.target?.result;
  };
  if(file.type.includes('image')) reader.readAsDataURL(file);
  loadedFileName.value = file.name;
  loadedFileSize.value = file.size;
  e.target.value = '';
};

const clear = () => {
  loadedImage.value = null;
  loadedFileName.value = '';
  loadedFileSize.value = 0;
};

const removeFile = () => {
  clear();
  emit("change", null);
  emit("update:modelValue", null);
};

defineExpose({
  clear,
});

</script>

<template>
  <div class="app-uploader-file">
    <input
        :id="randomId"
        type="file"
        :accept="accept"
        @change="handleFileChange"
        class="app-uploader-file__input"
    />

    <div
        v-if="isFileSelected"
        class="app-uploader-file__selected"
    >
      <div class="app-uploader-file__selected-extension">
        {{ loadedFileExtension }}
      </div>

      <div class="app-uploader-file__selected-info">
        <div
            class="app-uploader-file__selected-name"
            :title="loadedFileName"
        >
          {{ loadedFileName }}
        </div>
        <div class="app-uploader-file__selected-status">
          <ok-icon class="app-uploader-file__selected-status-icon"/>
          Файл выбран
          <span class="app-uploader-file__selected-size">({{ loadedFileSizeLabel }})</span>
        </div>
      </div>

      <div class="app-uploader-file__selected-actions">
        <label
            :for="randomId"
            class="app-uploader-file__replace"
        >
          Заменить
        </label>
        <button
            type="button"
            class="app-uploader-file__remove"
            title="Убрать файл"
            @click="removeFile"
        >
          <trash-icon/>
        </button>
      </div>
    </div>

    <label
        v-else
        :for="randomId"
        class="app-uploader-file__label"
    >
      <img
          v-if="loadedImage"
          :src="loadedImage"
          class="app-uploader-file__image"
          alt=""
      />
      <slot name="label"/>

      <template v-if="!$slots.label">
        Загрузить файл
      </template>

      <span class="app-uploader-file__name">{{ loadedFileName }}</span>
    </label>
  </div>
</template>

<style
    lang="scss"
    src="./AppUploaderFile.scss"
/>
